import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchWithAuth } from '../../../services/api';
import { getBlockLabel, CondoType, getBlockArticle } from '../../../utils/terminology';

interface BlockFormModalProps {
  onClose: () => void;
  onCreated: () => void;
  customerId: string;
  condoType?: CondoType;
}

/**
 * Quick "add a block/quadra" modal — the counterpart to UnitFormModal for the parent
 * entity. Fields mirror src/pages/Blocks.tsx's inline form (name, floors), minus the
 * import/full-management extras that page has, since this is meant as a fast add from
 * within Operational View's "Unidades" cadastro list, not a replacement for that page.
 */
export const BlockFormModal: React.FC<BlockFormModalProps> = ({
  onClose,
  onCreated,
  customerId,
  condoType,
}) => {
  const blockLabel = getBlockLabel(condoType, false);
  // Asked, not guessed from the last letter: "Lote" ends in an 'e' and is masculine all the same.
  const blockArticle = getBlockArticle(condoType);
  const showFloors = condoType !== 'casa';

  const [name, setName] = useState('');
  const [floors, setFloors] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(`Nome d${blockArticle.definite} ${blockLabel.toLowerCase()} é obrigatório`);
      return;
    }

    let floorsValue = 1;
    if (showFloors) {
      floorsValue = parseInt(floors, 10);
      if (!Number.isFinite(floorsValue) || floorsValue < 1) {
        setError('Número de andares deve ser pelo menos 1');
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetchWithAuth('/api/v1/blocks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          floors: floorsValue,
          is_active: true,
          customer_id: customerId,
          condo_id: customerId,
        }),
      });

      if (response.ok) {
        toast.success(`${blockLabel} criado com sucesso!`);
        onCreated();
        onClose();
      } else {
        try {
          const errData = await response.json();
          setError(errData.error || errData.message || `Erro ao criar ${blockLabel.toLowerCase()}`);
        } catch {
          setError(`Erro ao criar ${blockLabel.toLowerCase()} (${response.status})`);
        }
      }
    } catch {
      setError(`Erro ao criar ${blockLabel.toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {blockArticle.ordinal} {blockLabel}
            </h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome d{blockArticle.definite} {blockLabel}
              </label>
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: A, B, Torre 1, etc."
              />
            </div>

            {showFloors && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número de andares</label>
                <input
                  type="number"
                  value={floors}
                  onChange={(e) => setFloors(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 10"
                  min={1}
                />
              </div>
            )}

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Criar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlockFormModal;
